import { Button, Loading } from "@mozioinc/ui";
import { useQuery, useMutation, queryOptions } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import posthog from "posthog-js";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { GeneralError, Reservation, Dialog } from "../components";
import { ReactComponent as CancelSvg } from "../components/assets/cancel.svg";
import { parkingQuery } from "../utils/query";
import { ReactComponent as WarningSvg } from "./assets/warning.svg";

export const searchParamsSchema = z.object({
  reservationId: z.string(),
});

type ReservationQueryProps = z.infer<typeof searchParamsSchema>;

export const reservationQueryOptions = ({
  reservationId,
}: ReservationQueryProps) =>
  queryOptions({
    queryKey: ["reservation", reservationId],
    queryFn: () =>
      parkingQuery.getReservation({
        reservationId,
      }),
  });

export const ReservationOverview = () => {
  const { t } = useTranslation("parking:manage-reservation");
  const { reservationId } = useSearch({
    from: "/manage-reservation",
  });
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const reservationQuery = useQuery(reservationQueryOptions({ reservationId }));
  const reservation =
    reservationQuery.data && "id" in reservationQuery.data
      ? reservationQuery.data
      : undefined;

  const cancelReservation = useMutation({
    mutationFn: parkingQuery.cancelReservation,
    onError: () => setShowErrorDialog(true),
    onSuccess: (data) => {
      if ("id" in data) {
        reservationQuery.refetch();
      }
    },
  });

  const handleOnCancel = (reason: string) => {
    posthog.capture("Parking: Cancelation Submitted", {
      order_id: reservationId,
      price_amount: reservation?.payment.totalPrice,
      price_currency: reservation?.payment.currency,
      cancelation_reason: reason,
    });

    cancelReservation.mutate({
      reservationId,
      reason,
    });
  };

  const cancelReservationError =
    (cancelReservation.data &&
      "error" in cancelReservation.data &&
      cancelReservation.data) ||
    null;

  const reservationError =
    (reservationQuery.data &&
      "error" in reservationQuery.data &&
      reservationQuery.data) ||
    null;

  if (reservationQuery.isLoading) {
    return <Loading className="h-screen" />;
  }

  if (cancelReservationError) {
    return (
      <GeneralError
        error={cancelReservationError.error}
        className="flex-1"
        title={t("Cancellation not allowed")}
        message={cancelReservationError.message}
      />
    );
  }

  if (reservationError) {
    return (
      <GeneralError
        error={reservationError.error}
        className="flex-1"
        title={t("Reservation not found")}
        message={reservationError.message}
      />
    );
  }

  return (
    <>
      {!reservationError && (
        <Reservation reservation={reservation} onCancel={handleOnCancel} />
      )}

      <Dialog
        open={cancelReservation.isPending}
        icon={<CancelSvg className="text-primary text-6xl" />}
      >
        <h2 className="mb-1 mt-7 text-2xl font-bold leading-9">
          {t("Processing cancelation")}
        </h2>
        <p className="mb-8">{t("Please don't close this window.")}</p>
      </Dialog>

      <Dialog
        error={true}
        open={showErrorDialog}
        icon={<WarningSvg className="text-7xl text-white" />}
      >
        <h2 className="mb-1 mt-7 text-2xl font-bold leading-9">
          {t("Your cancelation has failed")}
        </h2>
        <p className="mb-8 text-center">{t("Please try again")}</p>
        <Button className="w-full" onClick={() => setShowErrorDialog(false)}>
          {t("Close")}
        </Button>
      </Dialog>
    </>
  );
};
